import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useAcademy } from '@/components/academy/AcademyProvider';
import { AcademyChecklist } from '@/components/academy/AcademyChecklist';
import { AcademyDocuments } from '@/components/academy/AcademyDocuments';
import { AcademyTeam } from '@/components/academy/AcademyTeam';
import { RichText } from '@/components/academy/AcademyBlocks';
import {
  Loader2, Rocket, Bell, ShieldCheck, LifeBuoy, GraduationCap, PlayCircle, Clock, ArrowLeft, Library, ChevronRight,
} from 'lucide-react';

const ICONS = { Rocket, Bell, ShieldCheck, LifeBuoy };

function LessonCard({ lesson, hasTour, index }) {
  return (
    <Link
      to={`/academy/${lesson.id}`}
      className="group rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex flex-col hover:border-navy/40 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 animate-in fade-in slide-in-from-bottom-2"
      style={{ animationDelay: `${index * 40}ms`, animationFillMode: 'both' }}
      data-testid={`academy-lesson-${lesson.id}`}
    >
      <h3 className="font-ibm font-semibold text-sm text-navy dark:text-white group-hover:text-red-600 transition-colors">{lesson.title}</h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 font-ibm mt-1 flex-1"><RichText text={lesson.summary} /></p>
      <div className="flex items-center gap-3 mt-3 text-[11px] font-ibm text-slate-400">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{lesson.minutes} min</span>
        {hasTour && <span className="flex items-center gap-1 text-red-600 font-semibold"><PlayCircle className="h-3 w-3" />Interaktiv guide</span>}
        <ChevronRight className="h-3.5 w-3.5 ml-auto text-slate-300 group-hover:text-navy transition-colors" />
      </div>
    </Link>
  );
}

export default function AcademyPage() {
  const { content, completed, loadError, reload } = useAcademy();
  const { isStaff, canCreate } = useAuth();
  const navigate = useNavigate();

  if (!content && loadError) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 font-ibm text-slate-500" data-testid="academy-load-error">
        Kunde inte ladda Vapelarm Academy.
        <button onClick={reload} className="text-sm text-red-600 underline" data-testid="academy-retry-btn">Försök igen</button>
      </div>
    );
  }

  if (!content) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  const total = content.checklist.length;
  const done = content.checklist.filter(i => completed.includes(i.id)).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="academy-page">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-10 pt-6 pb-10 md:pt-10 md:pb-14">
          {isStaff() && (
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-xs font-ibm text-slate-300 hover:text-white mb-6" data-testid="academy-back-to-alarms">
              <ArrowLeft className="h-3.5 w-3.5" />Tillbaka till larmvyn
            </button>
          )}
          <div className="flex items-center gap-2 text-red-400 text-xs font-ibm font-semibold uppercase tracking-[0.2em]">
            <GraduationCap className="h-4 w-4" />Utbildning
          </div>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl tracking-tight mt-3" data-testid="academy-title">Vapelarm Academy</h1>
          <p className="text-slate-300 font-ibm text-base md:text-lg mt-2 max-w-xl">Lär dig använda Vapelarm Cloud – i din egen takt.</p>
          <div className="h-1 w-16 bg-red-500 mt-6" />
          <p className="text-xs text-slate-400 font-ibm mt-4" data-testid="academy-version">
            {done} av {total} steg klara · Material version {content.meta.academy_version} · Uppdaterad {content.meta.updated}
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-10 -mt-6 pb-16 space-y-12">
        <AcademyChecklist />

        {content.categories.map(cat => {
          const lessons = content.lessons.filter(l => l.category === cat.id);
          if (!lessons.length) return null;
          const Icon = ICONS[cat.icon] || Rocket;
          return (
            <section key={cat.id} data-testid={`academy-category-${cat.id}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-9 w-9 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-red-500" />
                </div>
                <div>
                  <h2 className="font-chivo font-black text-lg text-navy dark:text-white leading-tight">{cat.title}</h2>
                  <p className="text-xs text-slate-400 font-ibm">{cat.description}</p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {lessons.map((l, i) => <LessonCard key={l.id} lesson={l} index={i} hasTour={!!l.tour} />)}
              </div>
            </section>
          );
        })}

        <AcademyDocuments />

        {canCreate() && <AcademyTeam />}

        <Link to="/knowledge" className="flex items-center gap-4 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-5 hover:bg-white dark:hover:bg-slate-800 transition-colors" data-testid="academy-knowledge-link">
          <Library className="h-5 w-5 text-slate-400" />
          <div className="flex-1">
            <p className="font-ibm font-semibold text-sm text-navy dark:text-white">Fler resurser i Kunskapsbanken</p>
            <p className="text-xs text-slate-400 font-ibm">Åtgärdsplan, skyltar, informationsbrev till vårdnadshavare och mobilguide.</p>
          </div>
          <ChevronRight className="h-4 w-4 text-slate-300" />
        </Link>
      </div>
    </div>
  );
}
